import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import {
  FaClipboardList,
  FaCog,
  FaEuroSign,
  FaUser,
  FaClock,
  FaCalendarAlt,
  FaCheckCircle,
  FaEdit,
} from "react-icons/fa";
import { BsFileBarGraph } from "react-icons/bs";
import { FiTarget } from "react-icons/fi";
import { getToken } from "../../api/auth";
import {
  agentsKpi,
  getPerformanceParameters,
  updatePerformanceParameter,
} from "../../api/performance";
import { getAllUsers } from "../../api/user";
import {
  getAgentsKpiByDate,
  getControllersKpiByDate,
  getDriversKpiByDate,
  getDriverKpi,
  getControllerKpi,
} from "../../api/dashboard";
import "./Targets.css";

const Targets = () => {
  const token = getToken();
  const [kpiData, setKpiData] = useState([]);
  const [driversKpiData, setDriversKpiData] = useState([]);
  const [controllersKpiData, setControllersKpiData] = useState([]);
  const [performanceParams, setPerformanceParams] = useState([]);
  const [roleType, setRoleType] = useState("agents");

  // Utility function to format parameter names
  const formatParameterName = (paramName) => {
    const nameMap = {
      BonusPerCorrectOrder: "Bonus per Correct Order",
      DeductionPerIncorrectOrder: "Deduction per Incorrect Order",
      BonusPerCorrectLine: "Bonus per Correct Line",
      DeductionPerIncorrectLine: "Deduction per Incorrect Line",
      BonusPerCorrectWeight: "Bonus per Correct Weight",
      DeductionPerIncorrectWeight: "Deduction per Incorrect Weight",
      BonusPerCorrectVolume: "Bonus per Correct Volume",
      DeductionPerIncorrectVolume: "Deduction per Incorrect Volume",
      BonusPerCorrectQuantity: "Bonus per Correct Quantity",
      DeductionPerIncorrectQuantity: "Deduction per Incorrect Quantity",
    };
    return nameMap[paramName] || paramName;
  };

  // Get icon and color for parameter type
  const getParameterStyle = (paramName) => {
    if (paramName.includes("Bonus")) {
      return { icon: "✓", color: "#22c55e", bgColor: "rgba(34, 197, 94, 0.1)" };
    } else if (paramName.includes("Deduction")) {
      return { icon: "✗", color: "#ef4444", bgColor: "rgba(239, 68, 68, 0.1)" };
    }
    return { icon: "●", color: "#6366f1", bgColor: "rgba(99, 102, 241, 0.1)" };
  };

  // Map roleType to roleId
  const getRoleIdFromType = (type) => {
    const roleMap = {
      agents: 3,
      drivers: 4,
      controllers: 2,
    };
    return roleMap[type];
  };

  // Get users for current role
  const getUsersForCurrentRole = () => {
    const roleId = getRoleIdFromType(roleType);
    return users.filter((user) => user.roleId === roleId);
  };

  // Format role name for display
  const formatRoleName = (role) => {
    const names = {
      agents: "Agent",
      drivers: "Driver",
      controllers: "Controller",
    };
    return names[role] || role;
  };
  const [fileUrl, setFileUrl] = useState(null);
  const [users, setUsers] = useState([]);
  const [companyCode, setCompanyCode] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState({
    parameterName: "",
    performanceValue: "",
    username: "",
    companyCode: "",
    validFrom: "",
    validTo: "",
  });
  const [editFormData, setEditFormData] = useState({
    parameterName: "",
    performanceValue: "",
    username: "",
    companyCode: "",
    validFrom: "",
    validTo: "",
  });
  const [exportFormData, setExportFormData] = useState({
    fromDate: "",
    toDate: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [userFilter, setUserFilter] = useState("All");
  const [error, setError] = useState("");
  const [isExportLoading, setIsExportLoading] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Agents KPI data
        const kpiResponse = await agentsKpi();
        // console.log("KPI Response:", kpiResponse);  Debug API response
        // Normalize kpiData to ensure parametersUsed is always an array
        const normalizedKpiData = (kpiResponse.kpiData || []).map((item) => ({
          ...item,
          parametersUsed: item.parametersUsed || [],
        }));
        setKpiData(normalizedKpiData);

        // Fetch Drivers KPI data
        const driversResponse = await getDriverKpi();
        const normalizedDriversKpiData = (driversResponse.kpiData || []).map(
          (item) => ({
            ...item,
            parametersUsed: item.parametersUsed || [],
          }),
        );
        setDriversKpiData(normalizedDriversKpiData);

        // Fetch Controllers KPI data
        const controllersResponse = await getControllerKpi();
        const normalizedControllersKpiData = (
          controllersResponse.kpiData || []
        ).map((item) => ({
          ...item,
          parametersUsed: item.parametersUsed || [],
        }));
        setControllersKpiData(normalizedControllersKpiData);

        // Fetch performance parameters
        const paramsResponse = await getPerformanceParameters();
        const filteredParams = paramsResponse.performanceParameters.filter(
          (param) =>
            [
              "BonusPerCorrectOrder",
              "DeductionPerIncorrectOrder",
              "BonusPerCorrectLine",
              "DeductionPerIncorrectLine",
              "BonusPerCorrectWeight",
              "DeductionPerIncorrectWeight",
              "BonusPerCorrectVolume",
              "DeductionPerIncorrectVolume",
              "BonusPerCorrectQuantity",
              "DeductionPerIncorrectQuantity",
            ].includes(param.performanceType),
        );

        // Sort so Bonuses appear first, then Deductions; within groups, sort by label
        const sortedParams = filteredParams.sort((a, b) => {
          const aIsBonus = a.performanceType.includes("Bonus");
          const bIsBonus = b.performanceType.includes("Bonus");
          if (aIsBonus !== bIsBonus) return aIsBonus ? -1 : 1;
          return formatParameterName(a.performanceType).localeCompare(
            formatParameterName(b.performanceType),
          );
        });

        setPerformanceParams(sortedParams);

        // Fetch users (include roles 2, 3, 4: Controllers, Agents, Drivers)
        const usersResponse = await getAllUsers();
        const filteredUsers =
          usersResponse.filter((user) => [2, 3, 4].includes(user.roleId)) || [];
        setUsers(filteredUsers);

        // Extract company code from first user (all users have same company code)
        if (filteredUsers.length > 0) {
          const extractedCompanyCode = filteredUsers[0].companyCode;
          // console.log("Company Code from Users:", extractedCompanyCode);
          setCompanyCode(extractedCompanyCode);
        }
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
        console.error("Fetch error:", err);
      }
    };
    if (token) {
      fetchData();
    }
  }, [token]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Validate date string
  const isValidDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string" || dateStr.trim() === "") {
      return false;
    }
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  };

  // Format date to ISO string with milliseconds (e.g., 2025-09-15T07:13:54.555)
  const formatDate = (value) => {
    if (!value) return "";
    try {
      // value from datetime-local is already in local time (no timezone)
      const date = new Date(value);

      // Extract local components
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      const ms = String(date.getMilliseconds()).padStart(3, "0");

      // Build string in your backend’s expected format
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
    } catch {
      return "";
    }
  };

  // Format date for display (e.g., "September 15, 2025, 7:13:54 AM")
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Handle form input changes
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Auto-set time for date inputs
    if (name === "validFrom" || name === "validTo") {
      if (value) {
        const currentValue = formData[name];
        const datePart = value.split("T")[0];
        const timePart = value.split("T")[1];
        const currentDatePart = currentValue ? currentValue.split("T")[0] : "";

        // Check if only the date changed (not the time)
        const dateChanged = datePart !== currentDatePart;
        const timeIsDefault =
          !timePart || timePart === "00:00" || timePart.startsWith("00:00");

        if (dateChanged && timeIsDefault) {
          // Date changed and time is default, set our custom default time
          const defaultTime = name === "validFrom" ? "01:00" : "23:00";
          const dateTimeValue = `${datePart}T${defaultTime}`;
          const processedValue = formatDate(dateTimeValue);
          setFormData((prev) => ({ ...prev, [name]: processedValue }));
        } else {
          // User has manually set a time, keep their value
          const processedValue = formatDate(value);
          setFormData((prev) => ({ ...prev, [name]: processedValue }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: "" }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle export form input changes
  // Handle export form input changes
  const handleExportInputChange = (e) => {
    const { name, value } = e.target;

    // Auto-set time for date inputs
    if (name === "fromDate" || name === "toDate") {
      if (value) {
        const currentValue = exportFormData[name];
        const datePart = value.split("T")[0];
        const timePart = value.split("T")[1];
        const currentDatePart = currentValue ? currentValue.split("T")[0] : "";

        // Check if only the date changed (not the time)
        const dateChanged = datePart !== currentDatePart;
        const timeIsDefault =
          !timePart || timePart === "00:00" || timePart.startsWith("00:00");

        if (dateChanged && timeIsDefault) {
          // Date changed and time is default, set our custom default time
          const defaultTime = name === "fromDate" ? "01:00" : "23:00";
          const dateTimeValue = `${datePart}T${defaultTime}`;
          const processedValue = formatDate(dateTimeValue);
          setExportFormData((prev) => ({ ...prev, [name]: processedValue }));
        } else {
          // User has manually set a time, keep their value
          const processedValue = formatDate(value);
          setExportFormData((prev) => ({ ...prev, [name]: processedValue }));
        }
      } else {
        setExportFormData((prev) => ({ ...prev, [name]: "" }));
      }
    } else {
      setExportFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.parameterName ||
      !formData.performanceValue ||
      !formData.username ||
      !formData.validFrom ||
      !formData.validTo
    ) {
      setError("All fields are required");
      return;
    }
    if (isNaN(parseFloat(formData.performanceValue))) {
      setError("Performance value must be a valid number");
      return;
    }
    if (!isValidDate(formData.validFrom)) {
      setError("Valid From must be a valid date");
      return;
    }
    if (!isValidDate(formData.validTo)) {
      setError("Valid To must be a valid date");
      return;
    }
    if (new Date(formData.validFrom) >= new Date(formData.validTo)) {
      setError("Valid To must be after Valid From");
      return;
    }
    if (!companyCode) {
      setError("Company code not available. Please refresh the page.");
      return;
    }
    try {
      const payload = {
        parameterName: formData.parameterName,
        performanceValue: String(formData.performanceValue),
        username: formData.username,
        companyCode: companyCode,
        validFrom: formData.validFrom,
        validTo: formData.validTo,
      };
      // console.log("Submitting payload:", payload);
      await updatePerformanceParameter(payload);
      setIsModalOpen(false);
      setFormData({
        parameterName: "",
        performanceValue: "",
        username: "",
        companyCode: "",
        validFrom: "",
        validTo: "",
      });
      setError("");
      // Refresh KPI data for the current role
      if (roleType === "agents") {
        const kpiResponse = await agentsKpi();
        const normalizedKpiData = (kpiResponse.kpiData || []).map((item) => ({
          ...item,
          parametersUsed: item.parametersUsed || [],
        }));
        setKpiData(normalizedKpiData);
      } else if (roleType === "drivers") {
        const driversResponse = await getDriverKpi();
        const normalizedDriversKpiData = (driversResponse.kpiData || []).map(
          (item) => ({
            ...item,
            parametersUsed: item.parametersUsed || [],
          }),
        );
        setDriversKpiData(normalizedDriversKpiData);
      } else if (roleType === "controllers") {
        const controllersResponse = await getControllerKpi();
        const normalizedControllersKpiData = (
          controllersResponse.kpiData || []
        ).map((item) => ({
          ...item,
          parametersUsed: item.parametersUsed || [],
        }));
        setControllersKpiData(normalizedControllersKpiData);
      }
    } catch (err) {
      setError(
        "Failed to update target. Please check your inputs and try again.",
      );
      console.error("Error submitting form:", err);
    }
  };

  // Open edit modal with pre-filled data
  const openEditModal = (param) => {
    setEditFormData({
      parameterName: param.parameterName,
      performanceValue: param.value || param.performanceValue || "",
      username: selectedAgent.userId,
      companyCode: companyCode,
      validFrom: param.validFrom,
      validTo: param.validTo,
    });
    setIsEditModalOpen(true);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.performanceValue) {
      setError("Performance value is required");
      return;
    }
    if (isNaN(parseFloat(editFormData.performanceValue))) {
      setError("Performance value must be a valid number");
      return;
    }
    try {
      const payload = {
        parameterName: editFormData.parameterName,
        performanceValue: String(editFormData.performanceValue),
        username: editFormData.username,
        companyCode: editFormData.companyCode,
        validFrom: editFormData.validFrom,
        validTo: editFormData.validTo,
      };
      await updatePerformanceParameter(payload);
      setIsEditModalOpen(false);
      setEditFormData({
        parameterName: "",
        performanceValue: "",
        username: "",
        companyCode: "",
        validFrom: "",
        validTo: "",
      });
      setError("");
      // Refresh KPI data
      const kpiResponse = await agentsKpi();
      const normalizedKpiData = (kpiResponse.kpiData || []).map((item) => ({
        ...item,
        parametersUsed: item.parametersUsed || [],
      }));
      setKpiData(normalizedKpiData);
    } catch (err) {
      setError(
        "Failed to update parameter. Please check your inputs and try again.",
      );
      console.error("Error updating parameter:", err);
    }
  };

  // Handle export form submission
  const handleExportSubmit = async (e) => {
    e.preventDefault();
    if (!exportFormData.fromDate || !exportFormData.toDate) {
      setError("Both From Date and To Date are required");
      return;
    }
    if (!isValidDate(exportFormData.fromDate)) {
      setError("From Date must be a valid date");
      return;
    }
    if (!isValidDate(exportFormData.toDate)) {
      setError("To Date must be a valid date");
      return;
    }
    if (new Date(exportFormData.fromDate) >= new Date(exportFormData.toDate)) {
      setError("To Date must be after From Date");
      return;
    }
    try {
      setError(""); // Clear previous errors
      setIsExportLoading(true);

      // Call the appropriate export method based on role type
      let blob;
      let fileName;

      if (roleType === "agents") {
        blob = await getAgentsKpiByDate(
          exportFormData.fromDate,
          exportFormData.toDate,
        );
        fileName = "agents-kpi.xlsx";
      } else if (roleType === "drivers") {
        blob = await getDriversKpiByDate(
          exportFormData.fromDate,
          exportFormData.toDate,
        );
        fileName = "drivers-kpi.xlsx";
      } else if (roleType === "controllers") {
        blob = await getControllersKpiByDate(
          exportFormData.fromDate,
          exportFormData.toDate,
        );
        fileName = "controllers-kpi.xlsx";
      }

      if (!blob) {
        console.error("No file returned");
        setError("Failed to generate report. Please try again.");
        setIsExportLoading(false);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      setIsExportModalOpen(false);
      setExportFormData({ fromDate: "", toDate: "" });
      setIsExportLoading(false);
    } catch (err) {
      console.error("Error generating report:", err);
      setError(
        "Failed to generate report. Please check the date range and try again.",
      );
      setIsExportLoading(false);
    }
  };

  // Open details modal for selected agent
  const openDetailsModal = (agent) => {
    // console.log("Selected Agent:", agent);  Debug selected agent
    setSelectedAgent(agent);
    setIsDetailsModalOpen(true);
  };

  // Calculate net amount for a metric
  const calculateNetAmount = (correctTotal, incorrectTotal) => {
    return correctTotal - Math.abs(incorrectTotal); // Use absolute value of incorrectTotal
  };

  // Filter kpiData based on search term and user filter
  const filteredKpiData = kpiData.filter((agent) => {
    const matchesSearch = agent.userId
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesUser = userFilter === "All" || agent.userId === userFilter;
    return matchesSearch && matchesUser;
  });

  // Get current KPI data based on role type
  const getCurrentKpiData = () => {
    let dataToFilter = [];
    if (roleType === "agents") {
      dataToFilter = kpiData;
    } else if (roleType === "drivers") {
      dataToFilter = driversKpiData;
    } else if (roleType === "controllers") {
      dataToFilter = controllersKpiData;
    }

    return dataToFilter.filter((item) => {
      const matchesSearch = item.userId
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesUser = userFilter === "All" || item.userId === userFilter;
      return matchesSearch && matchesUser;
    });
  };

  return (
    <div className="targets-container">
      <div className="floating-bg floating-bg-1"></div>
      <div className="floating-bg floating-bg-2"></div>
      <div className="floating-bg floating-bg-3"></div>
      <div className="page-header">
        <h2 className="targets-title">Performance Targets</h2>
        <p className="page-subtitle">Individual performance metrics and KPI</p>
      </div>
      {error && <div className="error-message">{error}</div>}

      {/* Role Type Tabs */}
      <div className="role-type-tabs">
        <button
          className={`role-tab ${roleType === "agents" ? "active" : ""}`}
          onClick={() => {
            setRoleType("agents");
            setUserFilter("All");
          }}
        >
          Agents
        </button>
        <button
          className={`role-tab ${roleType === "drivers" ? "active" : ""}`}
          onClick={() => {
            setRoleType("drivers");
            setUserFilter("All");
          }}
        >
          Drivers
        </button>
        <button
          className={`role-tab ${roleType === "controllers" ? "active" : ""}`}
          onClick={() => {
            setRoleType("controllers");
            setUserFilter("All");
          }}
        >
          Controllers
        </button>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search by username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        >
          <option value="All">All Users</option>
          {getUsersForCurrentRole().map((user) => (
            <option key={user.username} value={user.username}>
              {user.username}
            </option>
          ))}
        </select>
        <button
          className="glass-btn add-target-btn"
          onClick={() => {
            // Get today's date
            const today = new Date();
            const dateStr = today.toISOString().split("T")[0];

            // Set default times: 1 AM for validFrom, 11 PM for validTo
            const validFromDefault = formatDate(`${dateStr}T01:00`);
            const validToDefault = formatDate(`${dateStr}T23:00`);

            setFormData({
              parameterName: "",
              performanceValue: "",
              username: "",
              companyCode: "",
              validFrom: validFromDefault,
              validTo: validToDefault,
            });
            setIsModalOpen(true);
          }}
        >
          Create Target
        </button>
        <button
          className="glass-btn export-report-btn"
          onClick={() => {
            // Get today's date
            const today = new Date();
            const dateStr = today.toISOString().split("T")[0];

            // Set default times: 1 AM for fromDate, 11 PM for toDate
            const fromDateDefault = formatDate(`${dateStr}T01:00`);
            const toDateDefault = formatDate(`${dateStr}T23:00`);

            setExportFormData({
              fromDate: fromDateDefault,
              toDate: toDateDefault,
            });
            setIsExportModalOpen(true);
          }}
        >
          Export Report
        </button>
      </div>
      <div className="targets-grid">
        {getCurrentKpiData().length === 0 && (
          <div className="empty-state">
            <p>No targets found</p>
          </div>
        )}
        {getCurrentKpiData().map((agent) => {
          const ordersAmount = calculateNetAmount(
            agent.correctOrdersTotal,
            agent.incorrectOrdersTotal,
          );
          const lineAmount = calculateNetAmount(
            agent.correctLinesTotal,
            agent.incorrectLinesTotal,
          );
          const weightAmount = calculateNetAmount(
            agent.correctWeightTotal,
            agent.incorrectWeightTotal,
          );
          const volumeAmount = calculateNetAmount(
            agent.correctVolumeTotal,
            agent.incorrectVolumeTotal,
          );
          const quantityAmount = calculateNetAmount(
            agent.correctQuantityTotal,
            agent.incorrectQuantityTotal,
          );

          return (
            <div key={agent.userId} className="target-card">
              <div className="target-card-header">
                <div className="agent-initials">
                  <img
                    src={`https://ui-avatars.com/api/?name=${agent.userId}`}
                    alt={agent.userId}
                    className="agent-avatar"
                  />
                </div>
                <div className="agent-basic-info">
                  <h3 className="agent-name">{agent.userId}</h3>
                </div>
              </div>
              <div className="target-card-body">
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Correct</th>
                      <th>Incorrect</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Orders</td>
                      <td>{agent.correctOrders}</td>
                      <td>{agent.incorrectOrders}</td>
                      <td
                        className={
                          ordersAmount >= 0
                            ? "amount-positive"
                            : "amount-negative"
                        }
                      >
                        €{ordersAmount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Lines</td>
                      <td>{agent.correctLines}</td>
                      <td>{agent.incorrectLines}</td>
                      <td
                        className={
                          lineAmount >= 0
                            ? "amount-positive"
                            : "amount-negative"
                        }
                      >
                        €{lineAmount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Quantity</td>
                      <td>{agent.correctQuantity}</td>
                      <td>{agent.incorrectQuantity}</td>
                      <td
                        className={
                          quantityAmount >= 0
                            ? "amount-positive"
                            : "amount-negative"
                        }
                      >
                        €{quantityAmount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Weight</td>
                      <td>{agent.correctWeight}</td>
                      <td>{agent.incorrectWeight}</td>
                      <td
                        className={
                          weightAmount >= 0
                            ? "amount-positive"
                            : "amount-negative"
                        }
                      >
                        €{weightAmount.toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Volume</td>
                      <td>{agent.correctVolume}</td>
                      <td>{agent.incorrectVolume}</td>
                      <td
                        className={
                          volumeAmount >= 0
                            ? "amount-positive"
                            : "amount-negative"
                        }
                      >
                        €{volumeAmount.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="total-row">
                      <td>Total</td>
                      <td></td>
                      <td></td>
                      <td
                        className={
                          agent.bonus >= 0
                            ? "amount-positive"
                            : "amount-negative"
                        }
                      >
                        €{agent.bonus.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="target-card-actions">
                <button
                  className="glass-btn btn-primary"
                  onClick={() => openDetailsModal(agent)}
                >
                  View More
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Target Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div
            className="modal-content create-target-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-modal-header">
              <div className="create-header-content">
                <div className="create-icon-wrapper">
                  <FiTarget className="create-icon" />
                </div>
                <div>
                  <h3>Create New Target</h3>
                  <p className="create-subtitle">
                    Set performance parameters for {roleType}
                  </p>
                </div>
              </div>
              <button
                className="create-close-btn"
                onClick={() => setIsModalOpen(false)}
                type="button"
              >
                ×
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="create-target-form">
              <div className="form-section">
                <div className="section-title">
                  <FaClipboardList className="section-icon" />
                  <span>Parameter Details</span>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="parameterName">
                      Performance Parameter{" "}
                      <span className="required-star">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaCog className="input-icon" />
                      <select
                        id="parameterName"
                        name="parameterName"
                        value={formData.parameterName}
                        onChange={handleInputChange}
                        required
                        className="styled-select"
                      >
                        <option value="">Choose a parameter type...</option>
                        {performanceParams.map((param) => (
                          <option key={param.id} value={param.performanceType}>
                            {formatParameterName(param.performanceType)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="performanceValue">
                      Value (€) <span className="required-star">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaEuroSign className="input-icon" />
                      <input
                        type="number"
                        id="performanceValue"
                        name="performanceValue"
                        value={formData.performanceValue}
                        onChange={handleInputChange}
                        required
                        step="0.01"
                        placeholder="0.00"
                        className="styled-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="username">
                      {formatRoleName(roleType)}{" "}
                      <span className="required-star">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaUser className="input-icon" />
                      <select
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        className="styled-select"
                      >
                        <option value="">
                          Select a {formatRoleName(roleType).toLowerCase()}...
                        </option>
                        {getUsersForCurrentRole().map((user) => (
                          <option key={user.username} value={user.username}>
                            {user.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-title">
                  <FaCalendarAlt className="section-icon" />
                  <span>Validity Period</span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="validFrom">
                      Valid From <span className="required-star">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaClock className="input-icon" />
                      <input
                        type="datetime-local"
                        id="validFrom"
                        name="validFrom"
                        value={
                          formData.validFrom
                            ? formData.validFrom.slice(0, 16)
                            : ""
                        }
                        onChange={handleInputChange}
                        required
                        className="styled-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="validTo">
                      Valid To <span className="required-star">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaClock className="input-icon" />
                      <input
                        type="datetime-local"
                        id="validTo"
                        name="validTo"
                        value={
                          formData.validTo ? formData.validTo.slice(0, 16) : ""
                        }
                        onChange={handleInputChange}
                        required
                        className="styled-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-actions create-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => {
                    setIsModalOpen(false);
                    setFormData({
                      parameterName: "",
                      performanceValue: "",
                      username: "",
                      companyCode: "",
                      validFrom: "",
                      validTo: "",
                    });
                    setError("");
                  }}
                >
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  className="glass-btn submit-btn create-submit-btn"
                >
                  <FaCheckCircle /> <span>Create Target</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Export Report Modal */}
      {isExportModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Export {formatRoleName(roleType)} KPI Report</h3>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleExportSubmit}>
              <div className="form-group">
                <label htmlFor="fromDate">From Date</label>
                <input
                  type="datetime-local"
                  id="fromDate"
                  name="fromDate"
                  value={
                    exportFormData.fromDate
                      ? exportFormData.fromDate.slice(0, 16)
                      : ""
                  }
                  onChange={handleExportInputChange}
                  required
                />
                {fileUrl && (
                  <a href={fileUrl} download="report.pdf">
                    Download File
                  </a>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="toDate">To Date</label>
                <input
                  type="datetime-local"
                  id="toDate"
                  name="toDate"
                  value={
                    exportFormData.toDate
                      ? exportFormData.toDate.slice(0, 16)
                      : ""
                  }
                  onChange={handleExportInputChange}
                  required
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => {
                    setIsExportModalOpen(false);
                    setExportFormData({ fromDate: "", toDate: "" });
                    setError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="glass-btn submit-btn"
                  disabled={isExportLoading}
                >
                  {isExportLoading ? "Generating..." : "Generate Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {isDetailsModalOpen && selectedAgent && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsDetailsModalOpen(false);
            setSelectedAgent(null);
          }}
        >
          <div
            className="modal-content details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="details-modal-header">
              <div className="details-agent-info">
                <img
                  src={`https://ui-avatars.com/api/?name=${selectedAgent.userId}&size=64&background=2c5db1&color=fff`}
                  alt={selectedAgent.userId}
                  className="details-agent-avatar"
                />
                <div>
                  <h3>Performance Parameters</h3>
                  <p className="details-agent-name">{selectedAgent.userId}</p>
                </div>
              </div>
              <button
                className="details-close-btn"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedAgent(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="details-summary-cards">
              <div className="summary-card">
                <div className="summary-label">Total Parameters</div>
                <div className="summary-value">
                  {selectedAgent.parametersUsed?.length || 0}
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Total Bonus</div>
                <div className="summary-value positive">
                  €
                  {selectedAgent.bonus >= 0
                    ? selectedAgent.bonus.toFixed(2)
                    : "0.00"}
                </div>
              </div>
            </div>

            <div className="details-parameters-container">
              {selectedAgent.parametersUsed &&
              selectedAgent.parametersUsed.length > 0 ? (
                <div className="parameters-grid">
                  {selectedAgent.parametersUsed.map((param, index) => {
                    const style = getParameterStyle(param.parameterName);
                    return (
                      <div
                        key={index}
                        className="parameter-card"
                        style={{ "--param-color": style.color }}
                      >
                        <div className="parameter-header">
                          <div
                            className="parameter-icon"
                            style={{
                              backgroundColor: style.bgColor,
                              color: style.color,
                            }}
                          >
                            {style.icon}
                          </div>
                          <div className="parameter-name">
                            {formatParameterName(param.parameterName)}
                          </div>
                          <button
                            className="edit-param-btn"
                            onClick={() => openEditModal(param)}
                            title="Edit parameter"
                          >
                            <FaEdit />
                          </button>
                        </div>
                        <div className="parameter-body">
                          <div className="parameter-value-display">
                            <span className="value-label">Value</span>
                            <span
                              className="value-amount"
                              style={{ color: style.color }}
                            >
                              €{param.value}
                            </span>
                          </div>
                          <div className="parameter-dates">
                            <div className="date-item">
                              <span className="date-label">Valid From</span>
                              <span className="date-value">
                                {formatDateDisplay(param.validFrom)}
                              </span>
                            </div>
                            <div className="date-item">
                              <span className="date-label">Valid To</span>
                              <span className="date-value">
                                {formatDateDisplay(param.validTo)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state-box">
                  <div className="empty-icon">
                    <BsFileBarGraph />
                  </div>
                  <p className="empty-text">
                    No parameters configured for this agent.
                  </p>
                  <p className="empty-subtext">
                    Create a target to get started.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Parameter Modal */}
      {isEditModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsEditModalOpen(false);
            setEditFormData({
              parameterName: "",
              performanceValue: "",
              username: "",
              companyCode: "",
              validFrom: "",
              validTo: "",
            });
            setError("");
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Parameter</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditFormData({
                    parameterName: "",
                    performanceValue: "",
                    username: "",
                    companyCode: "",
                    validFrom: "",
                    validTo: "",
                  });
                  setError("");
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="create-target-form">
              {error && <div className="error-banner">{error}</div>}

              <div className="form-group">
                <label className="form-label">Parameter Name</label>
                <input
                  type="text"
                  className="form-input input-disabled"
                  value={formatParameterName(editFormData.parameterName)}
                  disabled
                />
              </div>

              <div className="form-group">
                <label className="form-label">Agent</label>
                <input
                  type="text"
                  className="form-input input-disabled"
                  value={editFormData.username}
                  disabled
                />
              </div>

              <div className="form-group">
                <label className="form-label">Performance Value (€)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={editFormData.performanceValue}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      performanceValue: e.target.value,
                    })
                  }
                  placeholder="Enter performance value"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Valid From</label>
                  <input
                    type="text"
                    className="form-input input-disabled"
                    value={formatDateDisplay(editFormData.validFrom)}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Valid To</label>
                  <input
                    type="text"
                    className="form-input input-disabled"
                    value={formatDateDisplay(editFormData.validTo)}
                    disabled
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="glass-btn cancel-btn"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditFormData({
                      parameterName: "",
                      performanceValue: "",
                      username: "",
                      companyCode: "",
                      validFrom: "",
                      validTo: "",
                    });
                    setError("");
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="glass-btn submit-btn">
                  Update Parameter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Targets;
